import { Fragment, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { changeRuleField, createRuleDraft, isFieldSelectable, moveItem, takenFieldKeys, type NudgeRuleDraft } from '../nudge-rules';
import type { NudgeField } from '../types';
import { ADD_BUTTON, FIELD_MENU_ITEM, PANEL_CARD } from '../ui-chrome';
import { NudgeRuleRow } from './nudge-rule-row';

interface NudgeRulesEditorProps {
	rows: NudgeRuleDraft[];
	fields: NudgeField[];
	editable: boolean;
	/** The first thing wrong with the rows, once a save has been attempted. */
	error: string | null;
	onChange: (rows: NudgeRuleDraft[]) => void;
}

/**
 * The "When to nudge" card: the conditions, AND-ed in display order.
 *
 * A condition is used at most once, enforced by what the menus offer rather than by an error after
 * the fact: a field another row holds is disabled in both the row menus and "Add Condition".
 */
export const NudgeRulesEditor = ({ rows, fields, editable, error, onChange }: NudgeRulesEditorProps) => {
	const [dragIndex, setDragIndex] = useState<number | null>(null);
	const taken = takenFieldKeys(rows);

	const updateRow = (next: NudgeRuleDraft) => onChange(rows.map((row) => (row.id === next.id ? next : row)));
	const removeRow = (id: string) => onChange(rows.filter((row) => row.id !== id));
	const move = (from: number, to: number) => onChange(moveItem(rows, from, to));
	// A blank row (the one a new nudge opens with) is filled first, so adding never strands it.
	const addRow = (field: NudgeField) => {
		const blank = rows.find((row) => !row.field);
		if (blank) updateRow(changeRuleField(blank, field));
		else onChange([...rows, createRuleDraft(field)]);
	};

	const dropOn = (index: number) => {
		if (dragIndex !== null) move(dragIndex, index);
		setDragIndex(null);
	};

	return (
		<section className={PANEL_CARD} aria-labelledby='nudge-when-title'>
			<h3 id='nudge-when-title' className='text-sm leading-5 font-semibold text-ink'>
				When to nudge
			</h3>
			<p className='text-xs leading-4 text-gray-800'>The nudge fires as soon as these conditions are met.</p>

			{rows.length > 0 && (
				<div className='mt-4 flex flex-col gap-1.5'>
					{rows.map((row, index) => (
						<Fragment key={row.id}>
							<NudgeRuleRow
								row={row}
								index={index}
								fields={fields}
								takenFieldKeys={taken}
								editable={editable}
								isDragging={dragIndex === index}
								onChange={updateRow}
								onRemove={() => removeRow(row.id)}
								onMove={move}
								onDragStart={setDragIndex}
								onDragEnd={() => setDragIndex(null)}
								onDropOn={dropOn}
							/>
							{index < rows.length - 1 && (
								// Aligned under the field select: the handle is 22px plus the row's 8px gap.
								<span className='ml-[30px] flex h-6 w-fit items-center rounded-md border border-indigo-200 bg-indigo-50 px-2 text-xs font-medium text-indigo-600'>
									AND
								</span>
							)}
						</Fragment>
					))}
				</div>
			)}

			{error && (
				<p role='alert' className='mt-3 text-xs text-red-600'>
					{error}
				</p>
			)}

			<DropdownMenu>
				<DropdownMenuTrigger asChild disabled={!editable}>
					<Button type='button' variant='outline' className={`${ADD_BUTTON} mt-4`}>
						<Plus className='size-3.5' /> Add Condition
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align='start' className='w-[280px] rounded-xl p-1.5'>
					{fields.map((field) => (
						<DropdownMenuItem
							key={field.key}
							disabled={!isFieldSelectable(field, taken)}
							onSelect={() => addRow(field)}
							className={FIELD_MENU_ITEM}
						>
							{field.label}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		</section>
	);
};
